// Call List access control — the manager panel (Aug 31, 2026).
//
// Alex: "an on/off switch he can toggle so the team doesn't abuse this feature.
// The off would block the RMs and TLs from seeing it. He also wants a zip code
// filter, where he approves or selects zip codes that the RMs can call. We have
// to control that so our drone pilot is not running all over Atlanta."
//
// Two controls, both enforced server-side in the recanvass_* definer RPCs:
//   • Master switch — OFF hides the board from Restore Managers + Team Leads and
//     refuses their bookings. Managers are never locked out, so whoever flips it
//     off can always flip it back on.
//   • Approved ZIPs — when the list is EMPTY there is no restriction at all.
//     That is deliberate: it makes shipping this a no-op until Alex picks ZIPs.
//     The empty state says so in as many words, because an allowlist that reads
//     as "nothing is allowed" would be the dangerous way to misread it.
//
// The panel leads with the number that actually matters — how many of the live
// leads the current selection exposes — so approving a ZIP is a decision made
// against real counts rather than a guess.

import { useEffect, useMemo, useState } from "react";
import {
  fetchRecanvassAccess, setRecanvassEnabled, setRecanvassAllowedZips,
  type RecanvassAccess,
} from "../../lib/recanvass";

const INPUT =
  "px-2 py-1.5 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-300";

function fmtWhen(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export default function AccessPanel({ onChanged }: { onChanged?: () => void }) {
  const [access, setAccess] = useState<RecanvassAccess | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // draft selection — only written to the server on Save
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [zipSearch, setZipSearch] = useState("");

  const load = async () => {
    try {
      const a = await fetchRecanvassAccess();
      setAccess(a);
      setDraft(new Set(a.allowed_zips.map((z) => z.zip)));
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't load access settings.");
    }
  };
  useEffect(() => { void load(); }, []);

  const say = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 4000); };

  const saved = useMemo(
    () => new Set((access?.allowed_zips ?? []).map((z) => z.zip)),
    [access],
  );
  const dirty = useMemo(() => {
    if (draft.size !== saved.size) return true;
    for (const z of draft) if (!saved.has(z)) return true;
    return false;
  }, [draft, saved]);

  const totalActive = useMemo(
    () => (access?.zip_stats ?? []).reduce((n, z) => n + z.active, 0) + (access?.no_zip_leads ?? 0),
    [access],
  );
  // What the CURRENT draft would expose. Empty draft = everything.
  const draftCovers = useMemo(() => {
    if (!access) return 0;
    if (draft.size === 0) return totalActive;
    return access.zip_stats.filter((z) => draft.has(z.zip)).reduce((n, z) => n + z.active, 0);
  }, [access, draft, totalActive]);

  const rows = useMemo(() => {
    const q = zipSearch.trim().toLowerCase();
    const all = [...(access?.zip_stats ?? [])];
    all.sort((a, b) => b.active - a.active || a.zip.localeCompare(b.zip));
    if (!q) return all;
    return all.filter(
      (z) => z.zip.includes(q) || z.cities.some((c) => c.toLowerCase().includes(q)),
    );
  }, [access, zipSearch]);

  if (!access) return null;
  if (!access.can_manage) return null;

  const toggleZip = (zip: string) =>
    setDraft((prev) => {
      const n = new Set(prev);
      if (n.has(zip)) n.delete(zip); else n.add(zip);
      return n;
    });

  async function flipSwitch(next: boolean) {
    setBusy(true);
    try {
      await setRecanvassEnabled(next);
      say(next ? "Call List switched ON for reps." : "Call List switched OFF for reps.");
      await load();
      onChanged?.();
    } catch (e) { setErr(e instanceof Error ? e.message : "Couldn't change the switch."); }
    finally { setBusy(false); }
  }

  async function saveZips() {
    setBusy(true);
    try {
      const r = await setRecanvassAllowedZips([...draft]);
      say(
        r.zips.length === 0
          ? "ZIP restriction lifted — reps can call every ZIP."
          : `${r.zips.length} ZIP${r.zips.length === 1 ? "" : "s"} approved for reps.`,
      );
      await load();
      onChanged?.();
    } catch (e) { setErr(e instanceof Error ? e.message : "Couldn't save the ZIP list."); }
    finally { setBusy(false); }
  }

  const restricted = saved.size > 0;

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
      {/* header — always readable at a glance, even collapsed */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <span
          className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-semibold border ${
            access.enabled
              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
              : "bg-rose-50 text-rose-800 border-rose-200"
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${access.enabled ? "bg-emerald-500" : "bg-rose-500"}`} />
          {access.enabled ? "Call List is ON for reps" : "Call List is OFF for reps"}
        </span>

        <span
          className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
            restricted
              ? "bg-sky-50 text-sky-800 border-sky-200"
              : "bg-slate-50 text-slate-600 border-slate-200"
          }`}
        >
          {restricted
            ? `${saved.size} approved ZIP${saved.size === 1 ? "" : "s"}`
            : "All ZIPs — no restriction"}
        </span>

        {access.enabled_at && (
          <span className="text-xs text-slate-500">
            {access.enabled ? "on" : "off"} since {fmtWhen(access.enabled_at)}
            {access.enabled_by_name ? ` · ${access.enabled_by_name}` : ""}
          </span>
        )}

        <button
          onClick={() => setOpen((v) => !v)}
          className="ml-auto text-sm px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50"
        >
          {open ? "Close access controls" : "⚙ Access controls"}
        </button>
      </div>

      {flash && (
        <div className="px-4 pb-3 -mt-1 text-sm text-emerald-700">{flash}</div>
      )}
      {err && (
        <div className="px-4 pb-3 -mt-1 text-sm text-rose-700">{err}</div>
      )}

      {open && (
        <div className="border-t border-slate-100 px-4 py-4 space-y-5 bg-slate-50/60">
          {/* ── master switch ── */}
          <section>
            <h3 className="text-sm font-semibold text-slate-900">Master switch</h3>
            <p className="text-xs text-slate-600 mt-0.5 mb-2">
              Off hides the whole Call List from <strong>Restore Managers and Team Leads</strong> and
              refuses their bookings. Managers keep full access, so you can always switch it back on.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                disabled={busy || access.enabled}
                onClick={() => void flipSwitch(true)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border transition disabled:opacity-60 ${
                  access.enabled
                    ? "bg-emerald-600 border-emerald-600 text-white"
                    : "bg-white border-slate-200 hover:bg-slate-50"
                }`}
              >
                On
              </button>
              <button
                disabled={busy || !access.enabled}
                onClick={() => void flipSwitch(false)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border transition disabled:opacity-60 ${
                  !access.enabled
                    ? "bg-rose-600 border-rose-600 text-white"
                    : "bg-white border-slate-200 hover:bg-slate-50"
                }`}
              >
                Off
              </button>
              {!access.enabled && (
                <span className="text-xs text-rose-700">
                  Reps currently see a paused notice instead of the board.
                </span>
              )}
            </div>
          </section>

          {/* ── ZIP allowlist ── */}
          <section>
            <div className="flex flex-wrap items-baseline gap-2">
              <h3 className="text-sm font-semibold text-slate-900">Approved ZIP codes</h3>
              <span className="text-xs text-slate-500">
                keeps the drone pilot inside the area you picked
              </span>
            </div>
            <p className="text-xs text-slate-600 mt-0.5 mb-3">
              Reps may only call — and only book — addresses in these ZIPs.{" "}
              <strong>Leave it empty and there is no restriction:</strong> every ZIP is open.
              Managers always see the full list either way.
            </p>

            {/* live consequence of the current draft */}
            <div className="flex flex-wrap items-center gap-3 mb-3 px-3 py-2 rounded-lg border border-slate-200 bg-white">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                  Reps would see
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {draftCovers.toLocaleString()}{" "}
                  <span className="text-sm font-medium text-slate-500">
                    of {totalActive.toLocaleString()} live leads
                  </span>
                </div>
              </div>
              <div className="text-sm text-slate-600">
                {draft.size === 0
                  ? "No ZIP restriction."
                  : `${draft.size} ZIP${draft.size === 1 ? "" : "s"} selected.`}
              </div>
              {dirty && (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                  unsaved
                </span>
              )}
              <div className="ml-auto flex items-center gap-2">
                {dirty && (
                  <button
                    disabled={busy}
                    onClick={() => setDraft(new Set(saved))}
                    className="text-sm px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60"
                  >
                    Undo
                  </button>
                )}
                <button
                  disabled={busy || !dirty}
                  onClick={() => void saveZips()}
                  className="text-sm px-3 py-1.5 rounded-md bg-slate-900 text-white font-medium hover:bg-slate-800 disabled:opacity-40"
                >
                  {busy ? "Saving…" : "Save approved ZIPs"}
                </button>
              </div>
            </div>

            {draft.size > 0 && access.no_zip_leads > 0 && (
              <div className="mb-3 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-900">
                ⚠ {access.no_zip_leads} live lead{access.no_zip_leads === 1 ? " has" : "s have"} no ZIP
                on file, so {access.no_zip_leads === 1 ? "it" : "they"} can never match an approved
                ZIP and {access.no_zip_leads === 1 ? "stays" : "stay"} hidden from reps while any
                restriction is on. Fix the address on the deal to bring{" "}
                {access.no_zip_leads === 1 ? "it" : "them"} back.
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mb-2">
              <input
                value={zipSearch}
                onChange={(e) => setZipSearch(e.target.value)}
                placeholder="Filter ZIP or city…"
                className={INPUT + " w-56"}
              />
              <button
                onClick={() => setDraft(new Set(rows.map((r) => r.zip)))}
                className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50"
              >
                Select {zipSearch ? "filtered" : "all"}
              </button>
              <button
                onClick={() => setDraft(new Set())}
                className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50"
              >
                Clear (no restriction)
              </button>
            </div>

            <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
              {rows.length === 0 && (
                <div className="px-3 py-4 text-sm text-slate-500">No ZIP matches that filter.</div>
              )}
              {rows.map((z) => {
                const on = draft.has(z.zip);
                return (
                  <label
                    key={z.zip}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition ${
                      on ? "bg-sky-50" : "hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleZip(z.zip)}
                      className="w-4 h-4 accent-sky-600"
                    />
                    <span className="font-semibold text-slate-900 tabular-nums w-14">{z.zip}</span>
                    <span className="text-sm text-slate-600 truncate flex-1">
                      {z.cities.join(" · ") || "—"}
                    </span>
                    <span className="text-xs text-slate-500 whitespace-nowrap">
                      {z.active} live
                      {z.unassigned > 0 && (
                        <span className="text-amber-700"> · {z.unassigned} unassigned</span>
                      )}
                      {z.max_hail != null && (
                        <span className="text-slate-400"> · max {z.max_hail}″</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>

            {access.allowed_zips.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs text-slate-500 cursor-pointer">
                  Who approved what
                </summary>
                <ul className="mt-1 space-y-0.5">
                  {access.allowed_zips.map((z) => (
                    <li key={z.zip} className="text-xs text-slate-600">
                      <strong className="tabular-nums">{z.zip}</strong> — {z.added_by_name ?? "unknown"}{" "}
                      · {fmtWhen(z.added_at)}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
